/**
 * The step's stack items in the order the drill asks for (design §2): the
 * step's own stack, or a filter step's fetched stack. Random order is drawn
 * once per stack content (cached by signature), so re-evaluation never
 * reshuffles.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { orderArray } from 'utils/array';
import { componentNameSnakeCase } from 'shared/composables/strapi/useComponent';
import type { DrillSettings } from 'src/types/course/drill';
import type { StackItems } from '../utilities/stackItems';

type StackSourceParent =
  ({ __component?: string } & Record<string, unknown>) | null;

export interface UseStepContentOptions {
  /** step_type[0] of the loaded step (useDrillConfig's `stepType0`). */
  stepType0: Readonly<
    Ref<{ stack?: { stack_source?: unknown } | null } | null | undefined>
  >;
  stepDrill: Readonly<
    Ref<
      | { settings?: Pick<DrillSettings, 'question_order'> | null }
      | null
      | undefined
    >
  >;
  isFilterStackSource: Readonly<Ref<boolean>>;
  filterStackSource: Readonly<Ref<StackItems | null>>;
  isFilterStackSourceFetched: Readonly<Ref<boolean>>;
  userSectionDocId?: string;
}

export function useStepContent({
  stepType0,
  stepDrill,
  isFilterStackSource,
  filterStackSource,
  isFilterStackSourceFetched,
  userSectionDocId,
}: UseStepContentOptions) {
  // useStep also checked `documentStepRepo.value &&` here; stepType0 is read
  // off that same step, so it is undefined whenever the step is missing.
  const stackSourceParent = computed<StackSourceParent>(() => {
    const stackParent = (
      stepType0.value?.stack?.stack_source as unknown[] | undefined
    )?.[0];
    return (stackParent as StackSourceParent) ?? null;
  });

  /**
   * The drill content is loaded and, for a filter step, the filter answer is
   * in — only then is it known whether there is anything to practise.
   */
  const isStackReady = computed(
    () =>
      !!stepDrill.value &&
      (!isFilterStackSource.value ||
        !userSectionDocId ||
        isFilterStackSourceFetched.value),
  );

  const stackSource: ComputedRef<StackItems> = computed(() => {
    if (isFilterStackSource.value) return filterStackSource.value ?? [];

    const componentName = stackSourceParent?.value?.__component;
    if (!componentName) return [];

    const stackSourceItems =
      stackSourceParent?.value?.[
        `${componentNameSnakeCase(componentName)}_item`
      ];
    if (!stackSourceItems) return [];
    return stackSourceItems as StackItems;
  });

  const randomOrderCache = ref<StackItems | null>(null);
  const randomOrderSignature = ref('');

  const buildStackSignature = (
    source: StackItems | undefined | null,
  ): string => {
    if (!source?.length) return 'empty';
    return `${source.length}:${JSON.stringify(source)}`;
  };

  const orderedStackSource = computed<StackItems>(() => {
    const settings = stepDrill.value?.settings;
    const source = stackSource?.value ?? [];
    if (!settings) return source;

    switch (settings.question_order) {
      case 'Random': {
        const signature = buildStackSignature(source);
        if (randomOrderSignature.value !== signature) {
          randomOrderCache.value = orderArray(source, 'random');
          randomOrderSignature.value = signature;
        }
        return randomOrderCache.value ?? [];
      }
      case 'Top to Bottom':
        return orderArray(source, 'start-to-end');
      case 'Bottom to Top':
        return orderArray(source, 'end-to-start');
      default:
        return source;
    }
  });

  return { stackSource, orderedStackSource, isStackReady };
}

export type UseStepContentReturn = ReturnType<typeof useStepContent>;
